import type { Message, CalloutData } from '../features/chat/types/UIMessage';
import type { ChatMessage } from '../features/chat/types/Message';
import { toAvatarLabel } from './InboxHelper';

/**
 * Strip HTML tags and decode entities using the browser's native DOMParser.
 * Mirrors mobile's convertToPlainText (which uses sanitize-html in React Native).
 */
export function convertToPlainText(htmlString: string): string {
  if (!htmlString) return '';
  const doc = new DOMParser().parseFromString(htmlString, 'text/html');
  return doc.body.textContent ?? '';
}

/** Parsed result of a raw socket/API message string that may contain callout HTML. */
export interface ParsedMessage {
  message: string;
  isCallout: boolean;
  calloutData: CalloutData[];
}

const CALLOUT_REGEX =
  /<div[^>]*class="callout-content"[^>]*>[\s\S]*?<small>([^<]*)<\/small>[\s\S]*?<small[^>]*class="callout-time"[^>]*>([^<]*)<\/small>[\s\S]*?<div[^>]*class="callout-message"[^>]*>([\s\S]*?)<\/div>[\s\S]*?<\/div>\s*<\/div>/g;

/**
 * Parse a raw HTML message string into plain text + optional callout (replied-to) blocks.
 * Mirrors parseMessageData from the mobile socket handler.
 */
export function parseMessageData(htmlString: string): ParsedMessage {
  if (!htmlString?.includes('callout-content')) {
    return { message: convertToPlainText(htmlString), isCallout: false, calloutData: [] };
  }

  const calloutData: CalloutData[] = [];
  // Reset lastIndex before exec loop — regex is module-level so state persists between calls
  CALLOUT_REGEX.lastIndex = 0;
  let match;
  while ((match = CALLOUT_REGEX.exec(htmlString)) !== null) {
    calloutData.push({
      name: match[1].trim(),
      time: match[2].trim(),
      message: convertToPlainText(match[3]),
    });
  }

  CALLOUT_REGEX.lastIndex = 0;
  const remainingHtml = htmlString.replace(CALLOUT_REGEX, '');

  return {
    message: convertToPlainText(remainingHtml).trim(),
    isCallout: calloutData.length > 0,
    calloutData,
  };
}

/**
 * Detect whether a raw HTML message string contains an attachment.
 * Mirrors hasAttachment() from the mobile helper.
 */
export function hasAttachment(htmlString: string): boolean {
  if (!htmlString) return false;
  const hasImage = /<img[^>]*data-mediaType="image"[^>]*>/i.test(htmlString);
  const hasCommentAttachment = /class=['"]comment-attachment['"]/i.test(htmlString);
  const hasFileTag = /<file[^>]*>/i.test(htmlString);
  return hasImage || hasCommentAttachment || hasFileTag;
}

/**
 * Convert an ISO date string → "DD/MM/YYYY H:MMAM" format.
 * This is the format expected by DateDivider and the chat bubble components.
 */
export function formatTimestamp(isoDate: string): string {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return isoDate;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const h = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${day}/${month}/${year} ${h % 12 || 12}:${mm}${ampm}`;
}

/**
 * Map a raw ChatMessage from the API → the Message shape used by bubble components.
 *
 * Rules (mirroring mobile ChatWindowModel logic):
 * - userId === 0  → system/Administrator message (centred info text, no bubble)
 * - userId === currentInternalUserId → sent (right-aligned bubble)
 * - otherwise → received (left-aligned bubble)
 *
 * Note: messageType === 1 alone is NOT a reliable system-message indicator —
 * regular user messages can also carry messageType 1.
 */
export function mapChatMessage(msg: ChatMessage, currentInternalUserId: number): Message {
  if (msg.userId === 0) {
    return {
      id: msg.id.toString(),
      type: 'system',
      content: convertToPlainText(msg.message),
      timestamp: formatTimestamp(msg.createdAt),
    };
  }
  const parsed = parseMessageData(msg.message ?? '');
  const isSent = msg.userId === currentInternalUserId;
  const lower = msg.messageStatus?.toLowerCase();
  const validStatus = lower === 'sent' || lower === 'delivered' || lower === 'read';
  return {
    id: msg.id.toString(),
    type: isSent ? 'sent' : 'received',
    content: parsed.message || convertToPlainText(msg.message),
    calloutMessage: msg.calloutMessage || parsed.isCallout,
    calloutData: parsed.calloutData.length > 0 ? parsed.calloutData : undefined,
    isDeleted: msg.isDeleted || false,
    hasAttachment: msg.hasAttachment || hasAttachment(msg.message ?? ''),
    timestamp: formatTimestamp(msg.createdAt),
    isoTimestamp: msg.createdAt,
    userName: msg.senderName,
    avatarLabel: toAvatarLabel(msg.senderName),
    avatarImage: msg.avatar || undefined,
    status: isSent ? (validStatus ? (lower as Message['status']) : 'sent') : undefined,
  };
}
